import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, Check, Anchor, Phone } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVENT_TYPES = ["Birthday Cruise","Wedding Cruise","Engagement Party","Hens Party","Corporate Event","Christmas Party","NYE Cruise","Vivid Cruise","BYO Cruise","Private Charter","Other"];

export default function EnquiryForm({ compact = false, defaultEvent = "", sourcePage = "", defaultDate = "" }) {
 const [form, setForm] = useState({ name: "", email: "", phone: "", event_type: defaultEvent, event_date: defaultDate, guests: "", message: "" });
 const [submitting, setSubmitting] = useState(false);
 const [success, setSuccess] = useState(null); // { name, eventType }

 // Auto-pre-fill from URL ?date=YYYY-MM-DD and ?type=Xxx (e.g. when arriving from the
 // availability calendar's "Enquire about this date" CTA).
 useEffect(() => {
  if (typeof window === "undefined") return;
  const params = new URLSearchParams(window.location.search);
  const d = params.get("date");
  const t = params.get("type");
  setForm(f => ({
   ...f,
   event_date: f.event_date || (d && /^\d{4}-\d{2}-\d{2}$/.test(d) ? d : f.event_date),
   event_type: f.event_type || (t || f.event_type),
  }));
 }, []);

 const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

 const submit = async (e) => {
  e.preventDefault();
  if (!form.name || !form.email) { toast.error("Name and email are required"); return; }
  setSubmitting(true);
  try {
   await axios.post(`${API}/enquiries`, { ...form, source_page: sourcePage || window.location.pathname });
   const firstName = form.name.trim().split(/\s+/)[0];
   setSuccess({ name: firstName, eventType: form.event_type || "your event" });
   // brand-styled toast as a secondary signal
   toast.custom(() => (
    <div className="flex items-center gap-3 rounded-2xl bg-gradient-to-r from-[#0A1E3D] via-[#0F2A57] to-[#0A1E3D] text-white pl-3 pr-5 py-3 ring-1 ring-amber-300/40 shadow-[0_20px_60px_-10px_rgba(0,0,0,0.6)] backdrop-blur">
     <div className="grid place-items-center w-9 h-9 rounded-full bg-amber-300 text-[#0A1E3D]">
      <Check className="w-5 h-5" strokeWidth={3} />
     </div>
     <div>
      <div className="text-[10px] uppercase tracking-[0.28em] text-amber-300 font-bold">Enquiry sent</div>
      <div className="text-sm font-semibold">Magic incoming, {firstName} &middot; we&apos;ll reply within 24 hrs</div>
     </div>
    </div>
   ), { duration: 6000 });
   setForm({ name: "", email: "", phone: "", event_type: defaultEvent, event_date: "", guests: "", message: "" });
  } catch (err) {
   toast.error("Something went wrong. Please call 1300 100 502.");
  } finally { setSubmitting(false); }
 };

 // ─── SUCCESS STATE ───────────────────────────────────────────────────────────
 if (success) {
  return (
   <div
    className="relative overflow-hidden rounded-2xl border border-amber-200/60 p-8 lg:p-10 shadow-xl text-center"
    style={{ background: "radial-gradient(at 20% 0%, hsl(45,90%,92%) 0%, transparent 50%), radial-gradient(at 80% 100%, hsl(200,80%,90%) 0%, transparent 50%), linear-gradient(180deg, #fff 0%, hsl(35,42%,97%) 100%)" }}
    data-testid="enquiry-success"
   >
    {/* confetti dots */}
    <div className="pointer-events-none absolute inset-0" aria-hidden="true">
     {[
      { l: "8%", t: "12%", c: "bg-amber-300", s: "w-2 h-2", d: "0s" },
      { l: "22%", t: "70%", c: "bg-pink-400", s: "w-2.5 h-2.5", d: ".4s" },
      { l: "78%", t: "20%", c: "bg-sky-400", s: "w-2 h-2", d: ".2s" },
      { l: "90%", t: "65%", c: "bg-amber-400", s: "w-3 h-3", d: ".6s" },
      { l: "50%", t: "8%", c: "bg-purple-400", s: "w-2 h-2", d: ".3s" },
      { l: "12%", t: "45%", c: "bg-emerald-400", s: "w-2 h-2", d: ".5s" },
      { l: "85%", t: "42%", c: "bg-pink-300", s: "w-2 h-2", d: ".1s" },
     ].map((p, i) => (
      <span
       key={i}
       className={`absolute ${p.c} ${p.s} rounded-full opacity-80`}
       style={{ left: p.l, top: p.t, animation: `enq-float 3.4s ${p.d} ease-in-out infinite` }}
      />
     ))}
    </div>

    <style>{`
     @keyframes enq-float { 0%,100% { transform: translateY(0) rotate(0deg); } 50% { transform: translateY(-14px) rotate(20deg); } }
     @keyframes enq-pop { 0% { transform: scale(0) rotate(-45deg); opacity: 0; } 60% { transform: scale(1.15) rotate(10deg); opacity: 1; } 100% { transform: scale(1) rotate(0); opacity: 1; } }
     @keyframes enq-ring { 0% { transform: scale(0.6); opacity: 0.55; } 100% { transform: scale(1.6); opacity: 0; } }
    `}</style>

    {/* animated check */}
    <div className="relative inline-grid place-items-center mb-6">
     <span className="absolute inset-0 rounded-full bg-amber-300/40" style={{ animation: "enq-ring 2.2s ease-out infinite" }} />
     <span className="absolute inset-0 rounded-full bg-amber-300/30" style={{ animation: "enq-ring 2.2s ease-out 0.6s infinite" }} />
     <div className="relative grid place-items-center w-20 h-20 rounded-full bg-gradient-to-br from-amber-300 to-amber-500 shadow-[0_15px_40px_-10px_rgba(252,211,77,0.7)]" style={{ animation: "enq-pop 0.7s cubic-bezier(0.34, 1.56, 0.64, 1) both" }}>
      <Check className="w-10 h-10 text-[#0A1E3D]" strokeWidth={3.5} />
     </div>
    </div>

    <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.32em] text-pink-600 font-bold mb-3">
     <Sparkles className="w-3.5 h-3.5" /> Enquiry received
    </div>
    <h3 className="font-display text-3xl lg:text-4xl text-ink leading-[1.05]" data-testid="enquiry-success-headline">
     Magic incoming, <span className="text-pink italic">{success.name}</span>.
    </h3>
    <p className="text-ink-mute text-base mt-4 max-w-md mx-auto leading-relaxed">
     Our crew has your enquiry for <span className="font-semibold text-ink">{success.eventType}</span> and
     will reply within <span className="font-semibold text-ink">24 hours</span> with options, pricing and availability.
     Check your inbox (and spam, just in case).
    </p>

    {/* quick info chips */}
    <div className="mt-7 flex flex-wrap items-center justify-center gap-2">
     <span className="inline-flex items-center gap-2 rounded-full bg-white/80 ring-1 ring-black/5 px-3.5 py-1.5 text-xs font-semibold text-ink">
      <Anchor className="w-3.5 h-3.5 text-pink" /> 100% personal reply
     </span>
     <span className="inline-flex items-center gap-2 rounded-full bg-white/80 ring-1 ring-black/5 px-3.5 py-1.5 text-xs font-semibold text-ink">
      <Sparkles className="w-3.5 h-3.5 text-amber-500" /> Custom quote
     </span>
     <span className="inline-flex items-center gap-2 rounded-full bg-white/80 ring-1 ring-black/5 px-3.5 py-1.5 text-xs font-semibold text-ink">
      <Check className="w-3.5 h-3.5 text-emerald-500" /> No obligation
     </span>
    </div>

    {/* secondary CTAs */}
    <div className="mt-7 flex flex-wrap justify-center gap-3">
     <a
      href="tel:1300100502"
      data-testid="enquiry-success-call-btn"
      className="inline-flex items-center gap-2 rounded-full bg-[#0A1E3D] hover:bg-[#0F2A57] text-white font-bold uppercase tracking-[0.18em] text-[11px] px-5 py-3 transition-colors"
     >
      <Phone className="w-3.5 h-3.5" /> Call 1300 100 502
     </a>
     <button
      type="button"
      onClick={() => setSuccess(null)}
      data-testid="enquiry-success-send-another-btn"
      className="inline-flex items-center gap-2 rounded-full bg-white hover:bg-amber-50 text-[#0A1E3D] font-bold uppercase tracking-[0.18em] text-[11px] px-5 py-3 ring-1 ring-black/10 transition-colors"
     >
      Send another enquiry
     </button>
    </div>
   </div>
  );
 }

 // ─── DEFAULT FORM STATE ──────────────────────────────────────────────────────
 return (
  <form onSubmit={submit} className={`bg-white rounded-2xl border border-[hsla(222,20%,80%,0.6)] p-6 lg:p-8 ${compact ? "" : "lg:p-10"} shadow-xl`} data-testid="enquiry-form">
   <div className="mb-6">
    <div className="eyebrow">Enquire Now</div>
    <h3 className="font-display text-3xl lg:text-4xl mt-3 text-ink leading-[1.05]">Let&apos;s plan your <span className="text-pink italic">magic</span> day</h3>
    <p className="text-ink-mute text-sm mt-1.5">Reply within 24 hours &middot; No obligation &middot; Custom quote</p>
   </div>
   <div className={`grid gap-4 ${compact ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1 md:grid-cols-2"}`}>
    <div><label className="lbl">Name *</label><input className="field" value={form.name} onChange={e=>update('name', e.target.value)} placeholder="Your full name" required data-testid="enquiry-name" /></div>
    <div><label className="lbl">Email *</label><input className="field" type="email" value={form.email} onChange={e=>update('email', e.target.value)} placeholder="you@email.com" required data-testid="enquiry-email" /></div>
    <div><label className="lbl">Phone</label><input className="field" value={form.phone} onChange={e=>update('phone', e.target.value)} placeholder="04..." data-testid="enquiry-phone" /></div>
    <div><label className="lbl">Event Type</label>
     <select className="field" value={form.event_type} onChange={e=>update('event_type', e.target.value)} data-testid="enquiry-event-type">
      <option value="">Select event type</option>
      {EVENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
     </select>
    </div>
    <div><label className="lbl">Date</label><input className="field" type="date" value={form.event_date} onChange={e=>update('event_date', e.target.value)} data-testid="enquiry-date" /></div>
    <div><label className="lbl">Guests</label><input className="field" value={form.guests} onChange={e=>update('guests', e.target.value)} placeholder="e.g. 50" data-testid="enquiry-guests" /></div>
    <div className="md:col-span-2"><label className="lbl">Message</label><textarea className="field" rows={4} value={form.message} onChange={e=>update('message', e.target.value)} placeholder="Tell us what you&apos;re celebrating..." data-testid="enquiry-message" /></div>
   </div>
   <button type="submit" disabled={submitting} className="btn-magic w-full justify-center mt-6 disabled:opacity-60" data-testid="enquiry-submit">
    <Sparkles className="w-4 h-4" /> {submitting ? "Sending..." : "Send Enquiry"}
   </button>
  </form>
 );
}
